import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import { EBooleanSearchTypes, FcbUserGridDto, UserSearch, UtilisateurApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { BooleanColumn, EditButton, EmailButton, PhoneButton, SearchTablePage, ViewButton } from "../../../components";
import { useAbortableApiServiceFactory, useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IUserProfileListPageProps {}

export const UserProfileListPage: React.FunctionComponent<IUserProfileListPageProps> = props => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(UtilisateurApi);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: FcbUserGridDto) => (
          <ButtonContainer>
            <ViewButton minimal={true} onClick={() => history.push(`${ERoutes.profile}/${row.iduser}/detail/view`)} />
            <EditButton minimal={true} onClick={() => history.push(`${ERoutes.profile}/${row.iduser}/detail/edit`)} />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom",
        width: 200
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom",
        width: 200
      },
      {
        header: () => t(ETLCodes.Gsm),
        fieldName: "gsm",
        render: (value: FcbUserGridDto) => value.gsm && <PhoneButton phone={value.gsm} />
      },
      {
        header: () => t(ETLCodes.Tel),
        fieldName: "telephone",
        render: (value: FcbUserGridDto) => value.telephone && <PhoneButton phone={value.telephone} />
      },
      {
        header: () => t(ETLCodes.General_Email),
        fieldName: "email",
        render: (value: FcbUserGridDto) =>
          value.email && <EmailButton email={value.email} emailBcc={value.email} hideText={false} />
      },
      {
        header: () => t(ETLCodes.NotificationsHope),
        fieldName: "notifySuiviHope",
        autoFitContent: true,
        alignment: "center",
        computed: true,
        render: (row: FcbUserGridDto) => <BooleanColumn value={row.notifySuiviHope} />
      },
      {
        header: () => t(ETLCodes.NotificationsSmile),
        fieldName: "notifySuiviSmile",
        autoFitContent: true,
        alignment: "center",
        computed: true,
        render: (row: FcbUserGridDto) => <BooleanColumn value={row.notifySuiviSmile} />
      },
      {
        header: () => t(ETLCodes.NotificationsObservateur),
        fieldName: "notifySuiviObserver",
        autoFitContent: true,
        alignment: "center",
        computed: true,
        render: (row: FcbUserGridDto) => <BooleanColumn value={row.notifySuiviObserver} />
      }
    ],
    [history, t]
  );

  const getCriteriasFn = React.useCallback(() => api.utilisateurGetSearchCriterias({ includeListsValues: true }), [
    api
  ]);

  const apiFactory = useAbortableApiServiceFactory(UtilisateurApi);
  const lastAbortController = React.useRef<AbortController>();
  const searchFn = React.useCallback(
    (nextSearch?: UserSearch) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.utilisateurBaseSearch({ UserSearch: nextSearch });
    },
    [apiFactory]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={getCriteriasFn}
      searchFunction={searchFn}
      onAbort={onAbort}
      defaultCriterias={[
        {
          criteria: "Actif",
          searchMode: EBooleanSearchTypes.Equals,
          value: true
        }
      ]}
      breadCrumbs={[{ text: t(ETLCodes.Profils), route: ERoutes.users }]}
      sortKeys={{ nom: "ASC" }}
      searchStateInitialSearch={false}
    />
  );
};

export default UserProfileListPage;
